import { type ComponentProps, type ReactNode, useRef } from 'react';
import { useField } from 'react-final-form';
import { fieldSubscription, FormGroup, getVisibleFieldError, useFieldProps } from '@ff-it/form';
import { Box, type Size } from '@ff-it/ui';
import { RichTextEditor } from './RichTextEditor';
import type { MarkdownWrite, ReferenceMetadata } from './markdown/references';

type RichTextFieldProps = Pick<ComponentProps<typeof RichTextEditor>, 'toolbar' | 'onSubmit'> & {
  name: string;
  label?: ReactNode;
  id?: string;
  required?: boolean;
  size?: Size;
  help?: ReactNode;
  className?: string;
  placeholder?: string;
  attachments?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  testId?: string;
  references?: ReferenceMetadata;
};

export function RichTextField(props: RichTextFieldProps) {
  const { name, label, className, help, testId = `${name}-field`, size = 'sm', ...rest } = useFieldProps(props);
  const pendingUpload = useRef(false);
  const { input, meta } = useField<MarkdownWrite>(name, {
    allowNull: true,
    beforeSubmit: () => !pendingUpload.current,
    subscription: fieldSubscription,
  });

  const fieldProps = {
    ...input,
    valid: getVisibleFieldError(meta) ? false : undefined,
    ...rest,
  };

  return (
    <FormGroup className={className} label={label} id={rest.id} help={help} meta={meta} required={fieldProps.required}>
      <Box textSize={size}>
        <RichTextEditor
          {...fieldProps}
          testId={testId}
          onUploadStatusChange={(status) => {
            pendingUpload.current = status.pending;
          }}
        />
      </Box>
    </FormGroup>
  );
}
